import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TypedBaseListComponent, SharedModule, DataTableLocationId } from '@vendure/admin-ui/core';
import { GetLimitedProductVariantsDocument, ProductVariantFilterParameter } from '../generated-types';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';

@Component({
    selector: 'product-variant-list',
    templateUrl: './product-variant-list.component.html',
    styleUrls: ['./product-variant-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SharedModule],
})
export class ProductVariantListComponent extends TypedBaseListComponent<
    typeof GetLimitedProductVariantsDocument,
    'productVariants'
> {
    @Input() hideLanguageSelect = false;
    @Input() dataTableId: DataTableLocationId | undefined;
    readonly filters = this.createFilterCollection()
        .addIdFilter()
        .addDateFilters()
        .addFilters([
            {
                name: 'name',
                type: { kind: 'text' },
                label: _('common.name'),
                filterField: 'name',
            },
            {
                name: 'enabled',
                type: { kind: 'boolean' },
                label: _('common.enabled'),
                filterField: 'enabled',
            },
            {
                name: 'sku',
                type: { kind: 'text' },
                label: _('catalog.sku'),
                filterField: 'sku',
            },
            {
                name: 'price',
                type: { kind: 'number', inputType: 'currency' },
                label: _('common.price'),
                filterField: 'price',
            },
            {
                name: 'priceWithTax',
                type: { kind: 'number', inputType: 'currency' },
                label: _('common.price-with-tax'),
                filterField: 'priceWithTax',
            },
        ])
        .connectToRoute(this.route);

    readonly sorts = this.createSortCollection()
        .addSorts([
            { name: 'id' },
            { name: 'createdAt' },
            { name: 'updatedAt' },
            { name: 'name' },
            { name: 'sku' },
            { name: 'price' },
            { name: 'priceWithTax' },
        ])
        .connectToRoute(this.route);

    constructor() {
        super();
        super.configure({
            document: GetLimitedProductVariantsDocument,
            getItems: data => data.productVariants,
            setVariables: (skip, take) => {
                const searchTerm = this.searchTermControl.value;
                const filterParam: ProductVariantFilterParameter = { _and: [] };
                const filterInput = this.filters.createFilterInput();
                if (Object.keys(filterInput).length) {
                    filterParam._and?.push(filterInput);
                }
                if (searchTerm) {
                    filterParam._and?.push({
                        _or: [
                            {
                                name: { contains: searchTerm },
                            },
                            {
                                sku: { contains: searchTerm },
                            },
                        ],
                    });
                }
                return {
                    options: {
                        skip,
                        take,
                        filter: filterParam,
                        sort: this.sorts.createSortInput(),
                    },
                };
            },
            refreshListOnChanges: [this.filters.valueChanges, this.sorts.valueChanges],
        });
    }
}
